import { ReceiptMetadata, ExpenseLineItem } from './api';

export const DEFAULT_MILEAGE_RATE = 0.725;

export interface CategoryStyle {
  icon: string;
  label: string;
  bg: string;        // badge / banner background
  text: string;       // badge / banner text
  cardBg: string;     // subtle card tint
  border: string;     // card border accent
}

export const CATEGORY_STYLES: Record<string, CategoryStyle> = {
  Meals:                          { icon: '\uD83C\uDF7D\uFE0F', label: 'Meals',          bg: '#fbd38d', text: '#744210', cardBg: '#fffaf0', border: '#f6ad55' },
  Airfare:                        { icon: '\u2708\uFE0F',       label: 'Airfare',        bg: '#bee3f8', text: '#2a4365', cardBg: '#ebf8ff', border: '#63b3ed' },
  Lodging:                        { icon: '\uD83C\uDFE8',       label: 'Lodging',        bg: '#d6bcfa', text: '#44337a', cardBg: '#faf5ff', border: '#b794f4' },
  'Transp.':                      { icon: '\uD83D\uDE95',       label: 'Transport',      bg: '#c6f6d5', text: '#22543d', cardBg: '#f0fff4', border: '#68d391' },
  Transportation:                 { icon: '\uD83D\uDE95',       label: 'Transport',      bg: '#c6f6d5', text: '#22543d', cardBg: '#f0fff4', border: '#68d391' },
  Mileage:                        { icon: '\uD83D\uDEE3\uFE0F', label: 'Mileage',        bg: '#b2f5ea', text: '#234e52', cardBg: '#e6fffa', border: '#4fd1c5' },
  'Rental Car':                   { icon: '\uD83D\uDD11',       label: 'Rental Car',     bg: '#c3dafe', text: '#3c366b', cardBg: '#ebf4ff', border: '#7f9cf5' },
  'Conference Registration':      { icon: '\uD83C\uDFAB',       label: 'Conference',     bg: '#fefcbf', text: '#744210', cardBg: '#fffff0', border: '#ecc94b' },
  'Computer + Office Supplies':   { icon: '\uD83D\uDCBB',       label: 'Supplies',       bg: '#e2e8f0', text: '#2d3748', cardBg: '#f7fafc', border: '#a0aec0' },
  'Learning + Development':       { icon: '\uD83D\uDCDA',       label: 'Learning',       bg: '#fefcbf', text: '#744210', cardBg: '#fffff0', border: '#ecc94b' },
  'Memberships + Subscriptions':  { icon: '\uD83D\uDCCB',       label: 'Membership',     bg: '#fed7e2', text: '#702459', cardBg: '#fff5f7', border: '#f687b3' },
  'Service Fees':                 { icon: '\uD83D\uDCB3',       label: 'Service Fees',   bg: '#e9d8fd', text: '#44337a', cardBg: '#faf5ff', border: '#b794f4' },
  Alcohol:                        { icon: '\uD83C\uDF77',       label: 'Alcohol',        bg: '#fed7d7', text: '#742a2a', cardBg: '#fff5f5', border: '#fc8181' },
  Other:                          { icon: '\uD83D\uDCC4',       label: 'Other',          bg: '#e2e8f0', text: '#4a5568', cardBg: '#f7fafc', border: '#a0aec0' },
  Expense:                        { icon: '\uD83D\uDCC4',       label: 'Expense',        bg: '#e2e8f0', text: '#4a5568', cardBg: '#f7fafc', border: '#a0aec0' },
};

const DEFAULT_STYLE: CategoryStyle = CATEGORY_STYLES.Expense;

export function getCategoryStyle(category: string): CategoryStyle {
  return CATEGORY_STYLES[category] || DEFAULT_STYLE;
}

/**
 * Derive a category from a receipt's metadata (vendor name, is_directions flag).
 */
export function deriveCategory(receipt: ReceiptMetadata): string {
  if (receipt.is_directions) return 'Mileage';
  const v = (receipt.vendor_name || '').toLowerCase();
  if (/lyft|uber|taxi|cab|toll|parking|park\b|lot\s|garage/i.test(v)) return 'Transp.';
  if (/delta|united|american|southwest|frontier|spirit|jetblue|airline/i.test(v)) return 'Airfare';
  if (/hotel|inn|marriott|hilton|hyatt|hampton|residence|suites|lodge|lodg/i.test(v)) return 'Lodging';
  if (/hertz|avis|enterprise|budget|national|rental/i.test(v)) return 'Rental Car';
  if (receipt.vendor_name) return 'Meals';
  return 'Expense';
}

/** Build a line item from a receipt's metadata (category derived from vendor, amount/mileage filled). */
export function receiptToLineItem(r: ReceiptMetadata, reason: string, mileageRate = DEFAULT_MILEAGE_RATE): ExpenseLineItem {
  const base = {
    date: r.receipt_date || (r.upload_timestamp ? r.upload_timestamp.split('T')[0] : ''),
    vendor: r.vendor_name || '',
    reason,
    receipt_id: r.receipt_id,
  };
  if (r.is_directions) {
    const miles = r.total_mileage || null;
    return {
      ...base,
      category: 'Mileage',
      amount: miles ? Math.round(miles * mileageRate * 100) / 100 : null,
      mileage: miles,
    };
  }
  // deriveCategory returns 'Expense' as a display-only fallback, but that isn't
  // in the expense category dropdown. For line items we want 'Meals' as the
  // real default when vendor-name heuristics don't match anything.
  const derived = deriveCategory(r);
  const category = derived === 'Expense' ? 'Meals' : derived;
  return {
    ...base,
    category,
    amount: r.receipt_amount ?? null,
    mileage: null,
  };
}
